// ── Ollama Backend ──────────────────────────────────────────────────────────
// Connects to a local Ollama server on localhost:11434.
// For power users who have Ollama installed with larger models.

import type { AIBackend, AIBackendStatus, AIBackendType, AIModelOption } from '../types';
import { pickBestOllamaModel } from './modelRank';

const OLLAMA_URL = 'http://localhost:11434';
const STORAGE_KEY = 'velostruct-ai-ollama-model';

export class OllamaBackend implements AIBackend {
  readonly type: AIBackendType = 'ollama';
  readonly contextWindowTokens = 32768;
  private _ready = false;
  private _model = '';
  private _models: string[] = [];

  async checkAvailability(): Promise<AIBackendStatus> {
    try {
      const res = await fetch(`${OLLAMA_URL}/api/tags`, { signal: AbortSignal.timeout(2000) });
      if (!res.ok) return 'unavailable';
      const data = await res.json() as { models?: Array<{ name: string }> };
      this._models = (data.models ?? []).map(m => m.name).filter(Boolean);
      if (this._models.length === 0) return 'downloadable';

      const preferred = typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
      this._model = pickBestOllamaModel(this._models, preferred);
      this._ready = true;
      return 'ready';
    } catch {
      return 'unavailable';
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async initialize(_onProgress?: (pct: number) => void): Promise<void> {
    const status = await this.checkAvailability();
    if (status !== 'ready') {
      throw new Error('Ollama is not running or has no models. Install from https://ollama.ai and run: ollama pull qwen2.5:7b');
    }
  }

  async listModels(): Promise<AIModelOption[]> {
    if (this._models.length === 0) await this.checkAvailability();
    const best = this._models.length > 0 ? pickBestOllamaModel(this._models) : '';
    return this._models.map(name => ({
      id: name,
      label: name,
      recommended: name === best,
    }));
  }

  getSelectedModel(): string | null {
    return this._model || null;
  }

  setSelectedModel(id: string): void {
    this._model = id;
    this._ready = this._models.includes(id) || this._models.some(n => n.startsWith(id));
    try { localStorage.setItem(STORAGE_KEY, id); } catch { /* ignore */ }
  }

  async prompt(
    systemPrompt: string,
    userMessage: string,
    _responseSchema?: object,
    history: Array<{ role: 'user' | 'assistant'; content: string }> = [],
  ): Promise<string> {
    if (!this._ready) throw new Error('Ollama not initialized');

    const res = await fetch(`${OLLAMA_URL}/api/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        model: this._model,
        messages: [
          { role: 'system', content: systemPrompt },
          ...history,
          { role: 'user', content: userMessage },
        ],
        stream: false,
        format: 'json',
        options: { temperature: 0.2, num_ctx: 8192 },
      }),
    });

    if (!res.ok) throw new Error(`Ollama error: ${res.status}`);
    const data = await res.json() as { message?: { content?: string } };
    return data.message?.content ?? '{"operations":[],"reply":"No response from Ollama."}';
  }

  destroy(): void {
    this._ready = false;
  }
}
